import moment from 'moment';
import { hAxisOptions } from '../../../constants/analytics.constants';
import { miniGraphicColors, units } from '../../../constants/graphics.constants';
import { convertBytes, getTimezoneOffsetInSeconds } from '../../../utils';
import { initialValues } from '../../../utils/chartHelper';
import { isNumeric } from '../../../utils/number';

export const getNetworkOptions = (data, dates, colorIndex, chartMetric) => {
  let minValue =
    convertBytes(Number(Math.max(...data[1].values.map((val) => val[1]).flat())), units, chartMetric, true) *
    1.2;
  let maxValue =
    convertBytes(Number(Math.max(...data[0].values.map((val) => val[1]).flat())), units, chartMetric, true) *
    1.2;
  let minimum =
    data !== null && data.length !== 0 && data[1].values !== null && chartMetric !== '%'
      ? -Math.ceil(minValue) || -1
      : -100;
  let maximum =
    data !== null && data.length !== 0 && data[0].values !== null && chartMetric !== '%'
      ? Math.ceil(maxValue > 100 ? maxValue : 100) || 1
      : 100;
  return serverOptions(
    data,
    colorIndex,
    dates,
    chartMetric,
    { left: 40, right: 20, top: 5, bottom: 40 },
    maximum,
    minimum
  );
};

export const getInternetOptions = ({ data, dates, colorIndex, chartMetric }) => {
  let min;
  let max;
  let ticks;
  
  if (data !== null && data.length !== 0 && data[0].values !== null) {
    max = Number(Math.max(...data.map(item => item.values.map(val => val[1])).flat()));
  } else {
    max = 1;
  }
  
  if (max > 1) {
    max = max * 1.2;
  }
  
  if (chartMetric === 'Status') {
    ticks = [
      { v: 0, f: 'Down' },
      { v: 1, f: 'Up' }
    ];
  } else if (chartMetric === '%') {
    min = 0;
    max = 100;
    ticks = [0, 25, 50, 75, 100];
  }
  
  return serverOptions(
    data,
    colorIndex,
    dates,
    chartMetric,
    { height: '60%', left: 50, right: 0 },
    max,
    min,
    ticks,
  );
};

export const serverOptions = (data, colorIndex, dates, chartMetric, chartArea, max, min = 0, ticks) => {
  const hAxisMinVal = new Date(
    Math.floor(
      dates[2] !== null ? Date.now() + getTimezoneOffsetInSeconds(dates[3]) - dates[2] * 1000 : dates[0]
    )
  );
  
  return {
    animation: { startup: true, duration: 700, easing: 'out' },
    focusTarget: 'category',
    tooltip: { isHtml: true },
    colors: miniGraphicColors.slice(colorIndex).concat(miniGraphicColors.slice(0, colorIndex)),
    legend: { position: data.length > 1 ? 'bottom' : 'none' },
    width: '100%',
    hAxis: {
      minValue: hAxisMinVal,
      ...hAxisOptions,
      viewWindow: {
        min: hAxisMinVal,
      },
    },
    vAxis: {
      gridlines: {
        count: 4,
      },
      format: chartMetric === '%' ? `#'${chartMetric}'` : `#.#' ${chartMetric}'`,
      viewWindow: {
        min: min,
        max: max,
      },
      minValue: min,
      maxValue: max,
      ticks,
    },
    chartArea: chartArea,
  };
};

export const getChartOptions = (
  data,
  dates,
  colorIndex,
  allMetric,
  chartMetric,
  chartArea,
  percentMaxValue = false
) => {
  let maxValue =
    data !== null && data.length !== 0 && data[0].values !== null && (chartMetric !== '%' || percentMaxValue)
      ? Math.ceil(
          convertBytes(
            !!allMetric
              ? Number(Math.max(...allMetric.map((item) => Math.round(item.value))))
              : Number(Math.max(...data.map((it) => it.values.map((val) => val[1])).flat())),
            units,
            chartMetric,
            true
          ) * (!!allMetric ? 1 : 1.2)
        ) || 1
      : 100;
  return serverOptions(
    data,
    colorIndex,
    dates,
    chartMetric,
    chartArea ? chartArea : { left: 50, width: '94%', top: 5, bottom: 40 },
    maxValue
  );
};

export const getChartData = (data, dates, chartMetric, doubleChart = false, showUnits = false, getTooltipValue) => {
  return [
    [
      'Timestamp',
      { type: 'string', role: 'tooltip', p: { html: true } },
      ...(data.length === 1 ? [chartMetric] : data.map((it) => it.metricLabel || it.machine)),
    ],
    ...(data[0].values.length !== 0
      ? data[0].values.map((value, index) => [
          new Date(new Date(value[0]).getTime() + getTimezoneOffsetInSeconds(dates[3])),
          `<div class="chart-tooltip">${moment(value[0]).tz(dates[3]).format('MMM D, YYYY, HH:mm:ss')}${`${
            data.length > 1
              ? data
                  .map((content) => [
                    content.metricLabel || content.machine,
                    content.values[index] && content.values[index][1] !== null
                      ? Number(content.values[index][1].toFixed(2))
                      : 'No data',
                  ])
                  .sort((x, y) => {
                    return y[1] - x[1];
                  })
                  .map(
                    (content) => {
                      const valueParts = [content[1]];
                      
                      if (showUnits) {
                        const unit = isNumeric(chartMetric) ? units[chartMetric] : chartMetric;
                        
                        if (unit) {
                          valueParts.push(unit);
                        }
                      }
                      
                      return `<div>${content[0]}: <b>${valueParts.join('')}</b></div>`;
                    }
                  )
              : value
                  .slice(1)
                  .map(
                    (content) =>
                      `<div>${chartMetric === '%' ? 'Percent' : chartMetric}: <b>${getTooltipValue ? getTooltipValue(content) : content.toFixed(
                        2
                      )}</b></div>`
                  )
          }`.replaceAll(',', '')}</div>`,
          ...(data.length > 1
            ? data.map((value, valueIndex) =>
                value.values[index]
                  ? doubleChart
                    ? valueIndex === 0
                      ? value.values[index][1]
                      : -value.values[index][1]
                    : value.values[index][1]
                  : null
              )
            : [value[1]]),
        ])
      : initialValues(dates)),
  ];
};
